import { Spinner } from "@via/components/loading/loading.tsx";
import {
  useLinkProjectToEnvironment,
  useLinkedProjects,
  useUnlinkProjectFromEnvironment,
} from "@via/pages/account/profile/run-environments/project-sharing.api.tsx";
import { useProjectList } from "@via/pages/projects/projects.api.tsx";
import { useMemo, useState, type JSX } from "react";

// GOV-2: lets a run-environment owner share the environment with specific projects. Anyone who
// can run in a linked project can then select this environment. Sharing only adds access; it
// never removes any access a user already has. Rendered only when editing an existing
// environment, since a link needs the environment id.
export default function SharedWithProjects({
  profileClusterId,
}: {
  profileClusterId: number;
}): JSX.Element {
  const { data: linkedProjectIds = [], isLoading: isLoadingLinks } =
    useLinkedProjects(profileClusterId);
  const { data: projectPage, isLoading: isLoadingProjects } = useProjectList(
    0,
    1000
  );
  const { mutateAsync: linkProject, isPending: isLinking } =
    useLinkProjectToEnvironment();
  const { mutateAsync: unlinkProject, isPending: isUnlinking } =
    useUnlinkProjectFromEnvironment();

  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [removingId, setRemovingId] = useState<number | null>(null);

  const allProjects = useMemo(() => projectPage?.data ?? [], [projectPage]);
  const projectNamesById = useMemo(
    () => new Map(allProjects.map(p => [p.id, p.name])),
    [allProjects]
  );
  const linkedSet = useMemo(
    () => new Set(linkedProjectIds),
    [linkedProjectIds]
  );
  const selectableProjects = useMemo(
    () => allProjects.filter(p => !linkedSet.has(p.id)),
    [allProjects, linkedSet]
  );

  const onAdd = async () => {
    const projectId = parseInt(selectedProjectId, 10);
    if (!projectId) return;
    await linkProject({ profileClusterId, projectId });
    setSelectedProjectId("");
  };

  const onRemove = async (projectId: number) => {
    setRemovingId(projectId);
    try {
      await unlinkProject({ profileClusterId, projectId });
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="shared-with-projects">
      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Shared with projects
        </label>
        <p className="text-xs text-gray-500">
          Anyone who can run in a selected project can choose this run
          environment. Sharing only adds access; it never removes it.
        </p>
      </div>
      <div className="flex items-end gap-2">
        <select
          data-testid="shared-with-projects-select"
          value={selectedProjectId}
          onChange={e => setSelectedProjectId(e.target.value)}
          disabled={isLoadingProjects}
          className="mt-2 block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm">
          <option value="">Select a project to share with</option>
          {selectableProjects.map(p => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          data-testid="shared-with-projects-add"
          onClick={() => void onAdd()}
          disabled={!selectedProjectId || isLinking}
          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50">
          <Spinner loading={isLinking}>Add</Spinner>
        </button>
      </div>
      <div className="rounded-md border border-gray-200">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-900">
          Projects
        </div>
        {isLoadingLinks ? (
          <div className="p-4 text-sm text-gray-500">Loading...</div>
        ) : linkedProjectIds.length === 0 ? (
          <div className="p-4 text-sm text-gray-600">
            This run environment is not shared with any project.
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {linkedProjectIds.map(projectId => (
              <li
                key={projectId}
                data-testid={`shared-project-${projectId}`}
                className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                <div className="min-w-0 flex-1 truncate text-gray-900">
                  {projectNamesById.get(projectId) ?? `Project ${projectId}`}
                </div>
                <button
                  type="button"
                  data-testid={`shared-project-remove-${projectId}`}
                  onClick={() => void onRemove(projectId)}
                  disabled={isUnlinking && removingId === projectId}
                  className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50">
                  <Spinner loading={isUnlinking && removingId === projectId}>
                    Remove
                  </Spinner>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
